import React, { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, FlaskConical, Plus, Save, Trash2 } from "lucide-react";
import { LIST_OPS, NO_VALUE_OPS, OPERATOR_LABELS, StatusPill, conditionFieldSuggestions, errorText } from "./common";

// Builder: trigger -> conditions (ALL must match) -> ordered actions, with
// "wait" steps between them. Backed by /api/automations (automation_engine.py),
// which re-validates everything; the UI only guides.

const BLANK_ACTIONS = {
  create_task: { type: "create_task", title: "Follow up with {{lead.clinic_name}}", description: "", assignee_id: "", due_in_days: 1, priority: "Medium" },
  notify: { type: "notify", notification_type: "info", title: "", message: "" },
  assign_lead_owner: { type: "assign_lead_owner", user_id: "" },
  update_lead_field: { type: "update_lead_field", field: "priority", value: "" },
  add_tag: { type: "add_tag", tag: "" },
  send_email: { type: "send_email", template_id: "", to: "lead" },
  send_sms: { type: "send_sms", template_id: "", to: "lead" },
  send_webhook: { type: "send_webhook", endpoint_id: "" },
  enroll_in_sequence: { type: "enroll_in_sequence", sequence_id: "" },
  delay: { type: "delay", amount: 1, unit: "hours" },
};

export const emptyAutomation = () => ({ name: "New automation", description: "", trigger: "lead.created", conditions: [], actions: [], enabled: true, max_runs_per_hour: 100 });

const parseValue = (op, raw) => (LIST_OPS.includes(op) ? String(raw).split(",").map((v) => v.trim()).filter(Boolean) : raw);
const showValue = (op, value) => (Array.isArray(value) ? value.join(", ") : value ?? "");

function ActionEditor({ action, onChange, catalog }) {
  const set = (patch) => onChange({ ...action, ...patch });
  const templates = (catalog.templates || []).filter((t) => t.channel === (action.type === "send_sms" ? "sms" : "email"));
  const field = (label, control) => <label style={{ display: "block", marginBottom: 6 }}>{label}{control}</label>;
  switch (action.type) {
    case "delay":
      return (
        <div className="form-grid">
          {field("Wait for", <input type="number" min="1" data-testid="delay-amount" value={action.amount} onChange={(e) => set({ amount: Number(e.target.value) })} />)}
          {field("Unit", <select data-testid="delay-unit" value={action.unit} onChange={(e) => set({ unit: e.target.value })}>{(catalog.delay_units || []).map((u) => <option key={u} value={u}>{u}</option>)}</select>)}
        </div>
      );
    case "create_task":
      return (
        <div className="form-grid">
          {field("Task title", <input value={action.title} onChange={(e) => set({ title: e.target.value })} />)}
          {field("Description", <textarea value={action.description} onChange={(e) => set({ description: e.target.value })} />)}
          {field("Assignee", (
            <select value={action.assignee_id} onChange={(e) => set({ assignee_id: e.target.value })}>
              <option value="">Whoever triggered it</option>
              <option value="lead_owner">The lead's owner</option>
              {(catalog.team || []).map((m) => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
            </select>
          ))}
          {field("Due in (days)", <input type="number" min="0" max="365" value={action.due_in_days} onChange={(e) => set({ due_in_days: Number(e.target.value) })} />)}
          {field("Priority", <select value={action.priority} onChange={(e) => set({ priority: e.target.value })}>{(catalog.task_priorities || []).map((p) => <option key={p}>{p}</option>)}</select>)}
        </div>
      );
    case "notify":
      return (
        <div className="form-grid">
          {field("Type", <select value={action.notification_type} onChange={(e) => set({ notification_type: e.target.value })}><option value="info">Info</option><option value="success">Success</option><option value="warning">Warning</option></select>)}
          {field("Title", <input value={action.title} placeholder="Automation Notification" onChange={(e) => set({ title: e.target.value })} />)}
          {field("Message", <input value={action.message} onChange={(e) => set({ message: e.target.value })} />)}
        </div>
      );
    case "assign_lead_owner":
    case "assign_user": {
      const owners = (catalog.team || []).filter((m) => (catalog.lead_owner_roles || []).includes(m.role));
      return field("New owner (roles that work leads only)", (
        <select data-testid="assign-owner" value={action.user_id} onChange={(e) => set({ user_id: e.target.value })}>
          <option value="">Choose a team member</option>
          {owners.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.role})</option>)}
        </select>
      ));
    }
    case "update_lead_field":
      return (
        <div className="form-grid">
          {field("Lead field (allow-listed)", <select value={action.field} onChange={(e) => set({ field: e.target.value })}>{(catalog.lead_fields || []).map((f) => <option key={f}>{f}</option>)}</select>)}
          {field("New value", action.field === "status" ? (
            <select value={action.value} onChange={(e) => set({ value: e.target.value })}><option value="">Choose a stage</option>{(catalog.pipeline_stages || []).map((s) => <option key={s}>{s}</option>)}</select>
          ) : <input value={action.value} onChange={(e) => set({ value: e.target.value })} />)}
        </div>
      );
    case "add_tag":
      return field("Tag", <input value={action.tag} onChange={(e) => set({ tag: e.target.value })} />);
    case "send_email":
    case "send_sms":
      return (
        <div className="form-grid">
          {field("Recipient", <select value={action.to} onChange={(e) => set({ to: e.target.value })}><option value="lead">The lead</option><option value="client">The client</option></select>)}
          {field("Approved template", (
            <select data-testid="template-select" value={action.template_id} onChange={(e) => set({ template_id: e.target.value })}>
              <option value="">Choose a template</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.approved ? "" : "  (not approved yet)"}</option>)}
            </select>
          ))}
          <p className="muted" style={{ gridColumn: "1 / -1" }}>
            Messages only send from templates approved by a Sales Manager or Master Administrator. Opted-out recipients are always skipped.
            {action.template_id && !templates.find((t) => t.id === action.template_id)?.approved ? " This template is not approved, so the automation cannot be enabled yet." : ""}
          </p>
        </div>
      );
    case "send_webhook":
      return field("Webhook endpoint", (
        <select value={action.endpoint_id} onChange={(e) => set({ endpoint_id: e.target.value })}>
          <option value="">Choose an endpoint</option>
          {(catalog.webhook_endpoints || []).map((w) => <option key={w.id} value={w.id}>{w.name}{w.enabled ? "" : " (disabled)"}</option>)}
        </select>
      ));
    case "enroll_in_sequence":
      return (
        <>
          {field("Email/SMS sequence", (
            <select data-testid="sequence-select" value={action.sequence_id} onChange={(e) => set({ sequence_id: e.target.value })}>
              <option value="">Choose a sequence</option>
              {(catalog.sequences || []).map((q) => <option key={q.id} value={q.id}>{q.name}{q.enrollable ? "" : "  (not approved yet)"}</option>)}
            </select>
          ))}
          <p className="muted">Enrolls the lead; the sequence itself sends later (business hours, daily cap). Only approved sequences can be used, and opted-out leads are never enrolled.</p>
        </>
      );
    default:
      return <p className="muted">Unknown action type {action.type}</p>;
  }
}

export default function AutomationBuilder({ api, showNotice, catalog, initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || emptyAutomation());
  const [testResult, setTestResult] = useState(null);
  const [testLeadId, setTestLeadId] = useState("");
  const [addType, setAddType] = useState("create_task");
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm(initial || emptyAutomation()); setTestResult(null); }, [initial]);

  const eventMeta = useMemo(() => (catalog.events || []).find((e) => e.type === form.trigger), [catalog, form.trigger]);
  const suggestions = useMemo(() => conditionFieldSuggestions(eventMeta), [eventMeta]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setAction = (i, action) => set({ actions: form.actions.map((a, j) => (j === i ? action : a)) });
  const moveAction = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.actions.length) return;
    const next = [...form.actions];
    [next[i], next[j]] = [next[j], next[i]];
    set({ actions: next });
  };
  const setCondition = (i, patch) => set({ conditions: form.conditions.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  const payload = () => ({
    name: form.name, description: form.description, trigger: form.trigger, conditions: form.conditions,
    actions: form.actions, enabled: form.enabled, max_runs_per_hour: Number(form.max_runs_per_hour) || 100,
  });

  const save = async () => {
    setSaving(true);
    try {
      const res = form.id ? await api.put(`/automations/${form.id}`, { data: payload() }) : await api.post("/automations", { data: payload() });
      const problems = res.data.activation_errors;
      if (problems && problems.length) showNotice(`Saved, but kept disabled: ${problems.join("; ")}`, "warning");
      else showNotice("Automation saved", "success");
      onSaved();
    } catch (err) {
      showNotice(errorText(err, "Could not save the automation"), "error");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    try {
      const res = await api.post("/automations/test", { automation: payload(), lead_id: testLeadId || undefined });
      setTestResult(res.data);
    } catch (err) {
      setTestResult(null);
      showNotice(errorText(err, "Test failed"), "error");
    }
  };

  return (
    <section className="panel glass-panel" data-testid="automation-builder">
      <div className="panel-header"><h3>{form.id ? "Edit automation" : "New automation"}</h3></div>

      <div className="form-grid">
        <label>Name<input data-testid="automation-name-input" value={form.name} onChange={(e) => set({ name: e.target.value })} /></label>
        <label>Description<input value={form.description || ""} onChange={(e) => set({ description: e.target.value })} /></label>
        <label>1. When this happens (trigger)
          <select data-testid="automation-trigger-select" value={form.trigger} onChange={(e) => set({ trigger: e.target.value })}>
            {(catalog.events || []).map((e) => <option key={e.type} value={e.type}>{e.label}</option>)}
          </select>
        </label>
        <label>Max runs per hour (safety limit)<input type="number" min="1" value={form.max_runs_per_hour} onChange={(e) => set({ max_runs_per_hour: e.target.value })} /></label>
      </div>

      <div className="panel-header"><h4>2. Only if… (all conditions must match)</h4>
        <button className="ghost-btn small" data-testid="add-condition" onClick={() => set({ conditions: [...form.conditions, { field: suggestions[0] || "lead.status", op: "equals", value: "" }] })}><Plus size={14} /> Add condition</button>
      </div>
      <datalist id="condition-fields">{suggestions.map((s) => <option key={s} value={s} />)}</datalist>
      {!form.conditions.length && <p className="muted">No conditions: runs for every {eventMeta?.label?.toLowerCase() || "event"}.</p>}
      {form.conditions.map((c, i) => (
        <div key={i} className="compact-row" data-testid={`condition-${i}`}>
          <input list="condition-fields" value={c.field} style={{ minWidth: 180 }} onChange={(e) => setCondition(i, { field: e.target.value })} />
          <select value={c.op} onChange={(e) => setCondition(i, { op: e.target.value, value: parseValue(e.target.value, showValue(c.op, c.value)) })}>
            {(catalog.operators || Object.keys(OPERATOR_LABELS)).map((op) => <option key={op} value={op}>{OPERATOR_LABELS[op] || op}</option>)}
          </select>
          {!NO_VALUE_OPS.includes(c.op) && <input value={showValue(c.op, c.value)} placeholder="value" onChange={(e) => setCondition(i, { value: parseValue(c.op, e.target.value) })} />}
          <button className="ghost-btn small" aria-label="Remove condition" onClick={() => set({ conditions: form.conditions.filter((_, j) => j !== i) })}><Trash2 size={14} /></button>
        </div>
      ))}

      <div className="panel-header"><h4>3. Then do this, in order</h4>
        <span>
          <select value={addType} onChange={(e) => setAddType(e.target.value)} data-testid="add-action-type">
            {(catalog.actions || []).filter((a) => a.available).map((a) => <option key={a.type} value={a.type}>{a.label}</option>)}
          </select>
          <button className="ghost-btn small" data-testid="add-action" onClick={() => set({ actions: [...form.actions, { ...BLANK_ACTIONS[addType] }] })}><Plus size={14} /> Add step</button>
        </span>
      </div>
      {!form.actions.length && <p className="muted">No steps yet.</p>}
      {form.actions.map((a, i) => (
        <div key={i} className="panel" style={{ marginBottom: 8, padding: 10 }} data-testid={`action-${i}`}>
          <div className="compact-row">
            <strong>{i + 1}. {(catalog.actions || []).find((x) => x.type === a.type)?.label || a.type}</strong>
            <span style={{ marginLeft: "auto" }}>
              <button className="ghost-btn small" aria-label="Move up" onClick={() => moveAction(i, -1)}><ArrowUp size={14} /></button>
              <button className="ghost-btn small" aria-label="Move down" onClick={() => moveAction(i, 1)}><ArrowDown size={14} /></button>
              <button className="ghost-btn small" aria-label="Remove step" onClick={() => set({ actions: form.actions.filter((_, j) => j !== i) })}><Trash2 size={14} /></button>
            </span>
          </div>
          <ActionEditor action={a} catalog={catalog} onChange={(next) => setAction(i, next)} />
        </div>
      ))}
      <p className="muted">Text fields accept placeholders like {"{{lead.clinic_name}}"}, {"{{event.to_stage}}"} and {"{{client.email}}"}. Steps after a "wait" run later via the scheduler.</p>

      <label style={{ display: "block", margin: "10px 0" }}>
        <input type="checkbox" checked={!!form.enabled} onChange={(e) => set({ enabled: e.target.checked })} /> Enabled (paused automations never run)
      </label>

      <div className="compact-row">
        <input placeholder="Optional: real lead id for the test" value={testLeadId} onChange={(e) => setTestLeadId(e.target.value)} />
        <button className="ghost-btn" data-testid="automation-test-button" onClick={runTest}><FlaskConical size={16} /> Test with sample event</button>
        <button className="primary-btn" data-testid="automation-save-button" disabled={saving} onClick={save}><Save size={16} /> {form.id ? "Update" : "Save"}</button>
        <button className="ghost-btn" onClick={onCancel}>Cancel</button>
      </div>

      {testResult && (
        <div className="panel" style={{ marginTop: 10, padding: 10 }} data-testid="automation-test-result">
          <strong>{testResult.would_run ? "Would run" : "Would NOT run"}</strong>
          {" - nothing was sent or changed (dry run)."}
          {!!testResult.activation_errors?.length && <p style={{ color: "#fbbf24" }}>Cannot be enabled yet: {testResult.activation_errors.join("; ")}</p>}
          {testResult.conditions.map((c, i) => (
            <div key={i} className="compact-row"><StatusPill status={c.passed ? "ok" : "failed"} /><span>{c.field} {OPERATOR_LABELS[c.op] || c.op} {showValue(c.op, c.value)} (actual: {JSON.stringify(c.actual)})</span></div>
          ))}
          {testResult.steps.map((s) => (
            <div key={s.index} className="compact-row"><StatusPill status={s.status} /><span>{s.index + 1}. {s.detail}{s.runs_after && s.runs_after !== "0:00:00" ? ` (after ${s.runs_after})` : ""}</span></div>
          ))}
        </div>
      )}
    </section>
  );
}
